import React from "react";
import ReactDOM from "react-dom/client";
import axios from "axios";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { initAnalytics } from "@/lib/analytics";

// ── Sentry (iter513) ────────────────────────────────────────────────
// Boot BEFORE anything else so early React/network errors are caught.
// DSN comes from build-time env: leave REACT_APP_SENTRY_DSN blank to
// disable (preview / local dev). On native iOS we prefer @sentry/capacitor
// so the native crash reporter also picks up hard iOS crashes.
try {
  const dsn = (process.env.REACT_APP_SENTRY_DSN || "").trim();
  if (dsn) {
    const isNative = typeof window !== "undefined"
      && window.Capacitor
      && typeof window.Capacitor.isNativePlatform === "function"
      && window.Capacitor.isNativePlatform();
    const SentryReact = require("@sentry/react");
    const sentryOpts = {
      dsn,
      environment: process.env.REACT_APP_SENTRY_ENV || "production",
      release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
      tracesSampleRate: Number(process.env.REACT_APP_SENTRY_TRACES_RATE || 0.1),
      replaysSessionSampleRate: 0,   // no session replay by default
      replaysOnErrorSampleRate: 0,
      sendDefaultPii: false,          // no emails/IPs unless explicit
      denyUrls: [/localhost/, /127\.0\.0\.1/],
      // iter514 · Suppress known third-party noise so the dashboard
      // only surfaces actionable events. Each pattern targets a real
      // upstream bug that we cannot fix on our side.
      ignoreErrors: [
        // @capgo/capacitor-updater fires an unhandled rejection when
        // its background OTA check hits Capgo's 429 rate limit — its
        // error path awaits the Preferences plugin object directly,
        // which trips "<Plugin>.then() is not implemented on ios".
        // The app is unaffected (OTA check just skips this cycle).
        /Preferences\.then\(\) is not implemented on ios/,
        // Same underlying pattern for any other Capacitor plugin that
        // Capgo probes internally — keep this generic filter narrow
        // to `is not implemented on ios` so real "method X missing"
        // bugs in OUR code still surface.
        /is not implemented on ios/,
      ],
    };
    if (isNative) {
      const SentryCapacitor = require("@sentry/capacitor");
      SentryCapacitor.init({ ...sentryOpts }, SentryReact.init);
    } else {
      SentryReact.init(sentryOpts);
    }
  }
} catch (e) {
  // Never let Sentry init crash the app boot.
  console.warn("Sentry init failed:", e && e.message);
}

// ── Capgo unhandled-rejection swallow (iter514) ────────────────────
// `@capgo/capacitor-updater`'s automatic boot-time OTA check hits
// Capgo's 429 rate limit and its error path awaits the Preferences
// plugin object directly, which throws
// `"Preferences.then()" is not implemented on ios`. It's an unhandled
// Promise rejection — the app is unaffected, but every launch fires
// the global reject handler which pollutes Sentry AND crashes the
// browser's error console. Preventing the default suppresses both.
try {
  if (typeof window !== "undefined") {
    window.addEventListener("unhandledrejection", (ev) => {
      const msg = (ev && ev.reason && (ev.reason.message || String(ev.reason))) || "";
      if (/is not implemented on ios/.test(msg)) {
        ev.preventDefault();  // stops the default console/Sentry pipeline
      }
    });
  }
} catch { /* older webviews without the event — nothing to do */ }

// Boot PostHog before React mounts so the very first pageview is captured.
initAnalytics();

// Auth tokens live in httpOnly cookies. Every API call MUST send credentials
// so the browser attaches `dl_admin` (admin JWT) and `dl_session` (public
// user session) to same-origin requests automatically.
axios.defaults.withCredentials = true;

// Global request timeout — the axios default is "never", so a single
// stalled fetch can lock the UI forever on hotel-wifi / flaky 3G. 20s
// is generous for the slowest legitimate endpoint (story hydration on
// a cold DB) but bounded enough that "spinner of death" never happens.
axios.defaults.timeout = 20_000;

// iter640 · One FLYT account can now hold multiple school memberships.
// The active org is chosen client-side via the SchoolMembershipsPill on
// /profile and persisted in localStorage. Every /api/staff/* request
// sends it as X-Flyt-Active-Org so the backend resolves the correct
// membership + permission set for that scope.
try {
  const activeOrg = localStorage.getItem('flyt_active_org_id');
  if (activeOrg) axios.defaults.headers.common['X-Flyt-Active-Org'] = activeOrg;
} catch { /* private mode — header omitted, backend defaults to first membership */ }

// iOS Capacitor cross-origin fallback: when the app runs inside the iOS
// shell (origin = capacitor://localhost) cookies for flythouse.com get
// dropped by SameSite. The backend already supports Bearer auth as a
// fallback, so we attach the persisted session token globally — covers
// every `axios.get/post` call across the entire codebase without having
// to refactor each one.
//
// CRITICAL: register the interceptors SYNCHRONOUSLY at boot (before any
// request can be issued), then kick off the async token-load in the
// background. `getTokenSync()` returns null until Preferences resolves,
// which is fine — by the time React actually mounts + fires its first
// authenticated call, the token is in memory. Registering inside the
// `.then()` (as we did before) created a race where startup requests
// could go out unauthenticated on slow iOS launches.
import { loadStoredToken, getTokenSync, saveToken } from './lib/nativeSession';

axios.interceptors.request.use((config) => {
  const t = getTokenSync();
  if (t && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

axios.interceptors.response.use((res) => {
  const tok = res?.data?.session_token;
  if (tok) saveToken(tok);
  return res;
}, (error) => {
  // iter688l · GLOBAL crash guard. FastAPI 422s return `detail` as an
  // ARRAY of Pydantic error OBJECTS ({type,loc,msg,input,ctx,url}). Any
  // component that did `toast.error(err.response.data.detail)` or put it
  // in JSX would crash React (#31 "objects are not valid as a child").
  // Normalise `detail` to a friendly STRING here, once, for the whole
  // app — so a bad request can never white-screen the native app again.
  try {
    const d = error?.response?.data?.detail;
    if (Array.isArray(d)) {
      const msg = d.map((e) => e?.msg).filter(Boolean).join(' · ');
      error.response.data.detail = msg || 'That didn’t go through — please check your input and try again.';
    } else if (d && typeof d === 'object') {
      error.response.data.detail = d.msg || d.message || 'Something went wrong — please try again.';
    }
  } catch { /* never let the guard itself throw */ }
  return Promise.reject(error);
});

// iter354 · Instant-paint SWR cache for hot public content GETs —
// stories/athletes/network feeds render from the last payload in 0ms
// on native iOS, then silently refresh in the background.
import { installSwrCache } from './lib/swrCache';
installSwrCache();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
const renderApp = () => root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);

// iter354 · AWAIT the Capacitor Preferences token read before the first
// render. Previously this was fire-and-forget, so components mounting
// on boot (Agent Console, Profile panels) could fire their API calls
// BEFORE the Bearer token was in memory → 401 → "Could not load your
// Agent Console" even though the user was signed in. The read is a
// few ms on native and resolves instantly on web.
loadStoredToken().catch(() => null).finally(renderApp);
